import { useState } from 'react';
import { initRootHandle } from '../storage/FileSystemAdapter';
import { BTN_PRIMARY, TEXT_MUTED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BG_SHELL, ERROR_TEXT, FONT_BODY, FONT_LABEL, FONT_BUTTON, FONT_FAMILY_DISPLAY } from '../theme';

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    subtitle:     'Gestión de Seguridad de la Información',
    intro:        'Selecciona una carpeta en tu equipo donde se guardarán todos los datos del SGSI. Puede ser una carpeta sincronizada con OneDrive, SharePoint o Dropbox.',
    detail:       'Todos los datos se almacenan localmente como archivos JSON que tú controlas. No se envía nada a ningún servidor externo.',
    button:       'Seleccionar carpeta',
    loading:      'Abriendo…',
    error:        'No se pudo acceder a la carpeta. Inténtalo de nuevo.',
    notSupported: 'Tu navegador no es compatible. Usa Google Chrome o Microsoft Edge.',
  },
  en: {
    subtitle:     'Information Security Management',
    intro:        'Select a folder on your device to store all ISMS data. It can be a folder synced with OneDrive, SharePoint, or Dropbox.',
    detail:       'All data is stored locally as JSON files that you own and control. Nothing is sent to any external server.',
    button:       'Select folder',
    loading:      'Opening…',
    error:        'Could not access the folder. Please try again.',
    notSupported: 'Your browser is not supported. Please use Google Chrome or Microsoft Edge.',
  },
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SetupScreen({ lang, onReady }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const t = T[lang] || T.en;

  const handleSelect = async () => {
    if (!window.showDirectoryPicker) {
      setError(t.notSupported);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      await initRootHandle(dirHandle);
      onReady();
    } catch (err) {
      if (err.name !== 'AbortError') setError(t.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 52px)',
      background: BG_SHELL,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      boxSizing: 'border-box',
    }}>
      <div style={{
        background: BG_SURFACE,
        border: `1px solid ${BORDER_DEFAULT}`,
        borderRadius: 12,
        padding: '3rem 2.5rem',
        maxWidth: 480,
        width: '100%',
        textAlign: 'center',
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '0.35rem',
          justifyContent: 'center',
          marginBottom: '0.5rem',
        }}>
          <span style={{
            fontFamily: FONT_FAMILY_DISPLAY,
            color: TEXT_HEADING,
            fontWeight: 800,
            fontSize: '2rem',
          }}>AT.</span>
          <span style={{
            fontFamily: FONT_FAMILY_DISPLAY,
            color: BRAND_PRIMARY,
            fontWeight: 400,
            fontSize: '1.75rem',
          }}>· grc</span>
        </div>

        {/* Subtitle */}
        <p style={{
          ...FONT_LABEL,
          color: TEXT_MUTED,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          marginBottom: '2rem',
        }}>
          {t.subtitle}
        </p>

        {/* Intro text */}
        <p style={{
          ...FONT_BODY,
          color: TEXT_DEFAULT,
          lineHeight: 1.65,
          marginBottom: '1rem',
        }}>
          {t.intro}
        </p>
        <p style={{
          ...FONT_LABEL,
          color: TEXT_MUTED,
          lineHeight: 1.55,
          marginBottom: '2rem',
        }}>
          {t.detail}
        </p>

        {/* Error */}
        {error && (
          <p style={{
            ...FONT_LABEL,
            color: ERROR_TEXT,
            marginBottom: '1.25rem',
          }}>
            {error}
          </p>
        )}

        {/* CTA */}
        <button
          onClick={handleSelect}
          disabled={busy}
          style={{
            ...BTN_PRIMARY,
            ...FONT_BUTTON,
            background: busy ? '#4b7c61' : BTN_PRIMARY.background,
            border: 'none',
            borderRadius: 8,
            padding: '0.8rem 2.25rem',
            cursor: busy ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
          }}
        >
          {busy ? t.loading : t.button}
        </button>
      </div>
    </div>
  );
}
